/**
 * Determines if an object is null
 * @param {Object} obj object checked
 * @returns {Boolean} object is null
 */
export const isNull = (obj) => obj === null;

/**
 * Determines if an object is undefined
 * @param {Object} obj object checked
 * @returns {Boolean} object is undefined
 */
export const isUndefined = (obj) => typeof obj === 'undefined';

/**
 * Determines if an object is either null or undefined
 * @param {Object} obj object checked
 * @returns {Boolean} object is null or undefined
 */
export const isNullOrUndefined = (obj) => isNull(obj) || isUndefined(obj);

/**
 * Determines if an object is both defined and non-null
 * @param {Object} obj object checked
 * @returns {Boolean} object is not null or undefined
 */
export const isNotNullOrUndefined = (obj) => !isNull(obj) && !isUndefined(obj);

/**
 * Determines if an object is a function
 * @param {Object} obj object checked
 * @returns {Boolean} object is a function
 */
export const isFunction = (obj) => typeof obj === 'function';

/**
 * Gets the property of an object by path
 * @param {Object} obj object checked
 * @param {String} path object path as a string
 * @returns {String} object property
 */
const getPropertyByPath = (obj, path) =>
  path
    .split('.')
    .reduce(
      (o, x, i, s) =>
        !isNullOrUndefined(o[x]) ? o[x] : i === s.length - 1 ? undefined : {},
      obj
    );

/**
 * Gets the property of an object by callback
 * @param {Object} obj object checked
 * @param {Function} callback getter function for object property
 * @returns {String} object property
 */
const getPropertyByCallback = (obj, callback) => callback(obj);

/**
 * Gets the property of an object by either path or callback
 * @param {Object} obj object checked
 * @param {String|Function} getter can be either String or Function
 * @returns {String|Error} object property or error if not implemented
 */
export const getProperty = (obj, getter) => {
  switch (typeof getter) {
    case 'string':
      return getPropertyByPath(obj, getter);
    case 'function':
      return getPropertyByCallback(obj, getter);
    default:
      throw new Error('Not Implemented');
  }
};
/**
 * Regular expression that matches one or more of any digit charcter (0-9)
 */
const regexNumeric = /^\d+$/;

/**
 * Gets the type of an object
 * @param {Object} a object checked
 * @returns {String} Data type of the object 
 * @throws Will throw an error if not implemented
 */
export const getType = (a) => {
  if (
    typeof a === 'number' ||
    (typeof a === 'string' && regexNumeric.test(a))
  ) {
    return 'number';
  } else if (a instanceof Date) {
    return 'date';
  } else if (typeof a === 'string') {
    return 'string';
  } else {
    throw new Error(`getType: Not implemented ${typeof a}`);
  }
};
